/**
 * THE PLAYBOOK CITATION — one declaration, used by the prompt and the UI.
 *
 * When the agent follows a team playbook it says so, so a counselor can tell WHY
 * an answer led with what it did. Silent behaviour change is the worse option:
 * a playbook that produces bad answers has to be findable and correctable, and a
 * reader who cannot see that guidance was applied has no way to report it.
 *
 * ## Why the citation is not left in the answer text
 *
 * It ended up inside SAVED DRAFTS. A real one read:
 *
 *     Best regards,
 *     [Your Name]
 *     Following the team playbook: Deposit status first.
 *
 * A counselor copying that into an email sends our internal plumbing to a family.
 * `[Your Name]` has the same shape but is obviously a placeholder; this one reads
 * like part of the message.
 *
 * So the citation is LIFTED OUT of the body and rendered as interface chrome
 * beside the answer. That keeps the transparency and makes it structurally
 * impossible to paste — the same argument that made drafts safe by giving the
 * table no "sent" column. Stripping it only on save would have left the same
 * text one manual copy away.
 *
 * ## Why this lives in `shared/`
 *
 * The prompt tells the model what to emit; the UI has to find it again. Those are
 * two ends of one agreement, in trees that cannot import from each other — which
 * is exactly the shape that has already produced silent breakage on this feature
 * more than once. One declaration, so a change to the wording is a change to both
 * ends or it does not compile.
 *
 * Types and constants only. No imports.
 */

/**
 * The exact opening the model is told to use, and the only thing the parser
 * looks for. Kept as ordinary readable English rather than a machine marker: if
 * the lifting ever fails, what remains in the answer is a sentence a person can
 * understand, not a stray token.
 */
export const PLAYBOOK_CITATION_PREFIX = "Following the team playbook:";

/**
 * Matches the citation as a WHOLE LINE, at the end of the text.
 *
 * Anchored to a line rather than searched loosely on purpose: a playbook named in
 * the middle of a sentence is the model talking about a playbook, not citing one,
 * and cutting text out of the middle of an answer would be a worse failure than
 * leaving a citation in.
 *
 * Tolerates the model's usual decorations — a bullet, bold, a trailing full stop.
 */
const CITATION_LINE = new RegExp(
  String.raw`(?:^|\n)[ \t]*(?:[-*>]\s*)?(?:\*\*|__)?\s*` +
    PLAYBOOK_CITATION_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
    String.raw`\s*(.*?)\s*(?:\*\*|__)?[ \t]*$`,
  "i",
);

export interface CitedAnswer {
  /** The answer with the citation removed. This is what gets saved or copied. */
  body: string;
  /** The playbook names, in the order cited. Empty when none was cited. */
  playbooks: string[];
}

/**
 * Split a citation off the end of an answer.
 *
 * Returns the text unchanged when there is nothing to lift, so every caller can
 * use this unconditionally rather than deciding whether to.
 */
export function splitPlaybookCitation(text: string): CitedAnswer {
  if (!text) return { body: text, playbooks: [] };

  const lines = text.split("\n");
  const playbooks: string[] = [];

  // Walk backwards from the end. A citation is a closing line, and more than one
  // may be present when several playbooks applied. Blank lines between them are
  // skipped; anything else stops the walk, so nothing is ever cut out of the
  // middle of an answer.
  let end = lines.length;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (line.trim() === "") {
      if (end === i + 1) end = i;
      continue;
    }
    const match = CITATION_LINE.exec(line);
    if (!match) break;
    const named = (match[1] ?? "").replace(/[.\s]+$/, "").trim();
    if (named) playbooks.unshift(named);
    end = i;
  }

  if (playbooks.length === 0) return { body: text, playbooks: [] };
  return { body: lines.slice(0, end).join("\n").replace(/\s+$/, ""), playbooks };
}
